<template>
  <div class="st-configuration-field d-flex align-center">
    <div class="st-configuration-field__input flex-grow-1">
      <!-- inherited / override state -->
      <div v-if="inheritedLevel" class="text-caption grey--text">
        {{ $t('configurationForm.inheritedFrom', { level: inheritedLevel }) }}
      </div>
      <v-btn
        v-else-if="canClearOverride"
        text
        x-small
        class="px-1"
        @click="onClearOverride"
      >
        {{ $t('configurationForm.clearOverride') }}
      </v-btn>

      <!-- bool -->
      <v-switch
        v-if="header.input_type === 'bool'"
        v-model="boolValue"
        :label="header.name"
        :disabled="isReadonly"
        :hint="header.description"
        :persistent-hint="!!header.description"
        dense
        class="mt-1"
        hide-details="auto"
        @change="markDirty"
      />

      <!-- select -->
      <v-select
        v-else-if="header.input_type === 'select'"
        v-model="localValue"
        :label="labelWithRequired"
        :items="parsedOptions"
        :disabled="isReadonly"
        :rules="fieldRules"
        :hint="header.description"
        :persistent-hint="!!header.description"
        dense
        hide-details="auto"
        @change="markDirty"
      />

      <!-- radio -->
      <div v-else-if="header.input_type === 'radio'">
        <label class="v-label text-caption">{{ labelWithRequired }}</label>
        <v-radio-group
          v-model="localValue"
          :disabled="isReadonly"
          dense
          hide-details="auto"
          row
          class="mt-1"
          @change="markDirty"
        >
          <v-radio
            v-for="opt in parsedOptions"
            :key="opt"
            :label="opt"
            :value="opt"
          />
        </v-radio-group>
        <div v-if="header.description" class="text-caption grey--text mb-2">{{ header.description }}</div>
      </div>

      <!-- checkbox -->
      <div v-else-if="header.input_type === 'checkbox'">
        <label class="v-label text-caption">{{ labelWithRequired }}</label>
        <div class="d-flex flex-wrap mt-1">
          <v-checkbox
            v-for="opt in parsedOptions"
            :key="opt"
            v-model="checkboxValues"
            :label="opt"
            :value="opt"
            :disabled="isReadonly"
            dense
            hide-details
            class="mr-4 mt-0"
            @change="onCheckboxChange"
          />
        </div>
        <div v-if="header.description" class="text-caption grey--text mb-2">{{ header.description }}</div>
      </div>

      <!-- textarea -->
      <v-textarea
        v-else-if="header.input_type === 'textarea'"
        v-model="localValue"
        :label="labelWithRequired"
        :disabled="isReadonly"
        :rules="fieldRules"
        :counter="inputOptions.max_length || false"
        :rows="inputOptions.rows || 3"
        :hint="header.description"
        :persistent-hint="!!header.description"
        auto-grow
        dense
        hide-details="auto"
        @input="markDirty"
      />

      <!-- date -->
      <STDatePickerV2
        v-else-if="header.input_type === 'date'"
        v-model="localValue"
        :label="labelWithRequired"
        :readonly="isReadonly"
        :rules="fieldRules"
        :hint="header.description"
        :persistent-hint="!!header.description"
        default-empty
        dense
        hide-details="auto"
        @input="onDateInput"
      />

      <!-- time -->
      <STTimePicker
        v-else-if="header.input_type === 'time'"
        v-model="localValue"
        :label="labelWithRequired"
        :disabled="isReadonly"
        :rules="fieldRules"
        dense
        @input="onTimeInput"
      />

      <!-- datetime -->
      <div v-else-if="header.input_type === 'datetime'" class="d-flex align-start" style="gap: 16px;">
        <STDatePickerV2
          v-model="dateTimeParts.date"
          :label="labelWithRequired"
          :readonly="isReadonly"
          :rules="fieldRules"
          default-empty
          dense
          hide-details="auto"
          @input="onDateTimeInput"
        />
        <STTimePicker
          v-model="dateTimeParts.time"
          :label="$t('configurationForm.time')"
          :disabled="isReadonly"
          dense
          style="max-width: 140px"
          @input="onDateTimeInput"
        />
      </div>

      <!-- int -->
      <v-text-field
        v-else-if="header.input_type === 'int'"
        v-model="localValue"
        :label="labelWithRequired"
        :disabled="isReadonly"
        :rules="fieldRules"
        :hint="header.description"
        :persistent-hint="!!header.description"
        type="number"
        dense
        hide-details="auto"
        @input="markDirty"
      />

      <!-- email -->
      <v-text-field
        v-else-if="header.input_type === 'email'"
        v-model="localValue"
        :label="labelWithRequired"
        :disabled="isReadonly"
        :rules="fieldRules"
        :hint="header.description || (header.multiple_value ? $t('configurationForm.emailHintMultiple') : $t('configurationForm.emailHintSingle'))"
        persistent-hint
        dense
        hide-details="auto"
        @input="markDirty"
      />

      <!-- file -->
      <div v-else-if="header.input_type === 'file'">
        <label class="v-label text-caption">{{ labelWithRequired }}</label>
        <div class="d-flex align-center mt-1">
          <v-file-input
            ref="fileInputRef"
            v-model="selectedFile"
            :label="localValue ? '' : $t('configurationForm.chooseFile')"
            :accept="inputOptions.accept || '*'"
            :disabled="isReadonly || uploading"
            :rules="fileRules"
            :hint="header.description"
            :persistent-hint="!!header.description"
            dense
            hide-details="auto"
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            class="flex-grow-1"
            @change="onFileSelected"
          />
          <v-progress-circular
            v-if="uploading"
            indeterminate
            size="20"
            width="2"
            color="primary"
            class="ml-2"
          />
        </div>
        <div v-if="localValue" class="text-caption mt-1 d-flex align-center">
          <v-icon x-small class="mr-1">mdi-file-check</v-icon>
          <span class="grey--text text--darken-1" style="word-break: break-all;">{{ fileName }}</span>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                x-small
                color="primary"
                class="ml-1"
                v-bind="attrs"
                v-on="on"
                @click="onDownloadFile"
              >
                <v-icon x-small>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('configurationForm.downloadFile') }}</span>
          </v-tooltip>
          <v-btn
            v-if="!isReadonly"
            icon
            x-small
            class="ml-1"
            @click="onClearFile"
          >
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- table (item lookup) -->
      <STItemLookup
        v-else-if="header.input_type === 'table'"
        v-model="tableDisplayText"
        :table="header.reference_table"
        :label="labelWithRequired"
        :headers="tableLookupHeaders"
        :select-item="onTableItemSelected"
        :item-key="tableLookupDisplayKey"
        :item-key-value="tableLookupDisplayKey"
        :rules="fieldRules"
        :disabled="isReadonly"
        :readonly="isReadonly"
        :hint="header.description"
        :persistent-hint="!!header.description"
        :addable="false"
        dense
      />

      <!-- string (default text) -->
      <v-text-field
        v-else
        v-model="localValue"
        :label="labelWithRequired"
        :disabled="isReadonly"
        :rules="fieldRules"
        :counter="inputOptions.max_length || false"
        :hint="header.description"
        :persistent-hint="!!header.description"
        dense
        hide-details="auto"
        @input="markDirty"
      />
    </div>

    <!-- Action buttons -->
    <div v-if="!isReadonly" class="st-configuration-field__actions d-flex align-center ml-2">
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn
              small
              depressed
              color="primary"
              :disabled="!isDirty || !isValid"
              min-width="0"
              class="px-1 mr-1"
              @click="onSave"
            >
              <v-icon small>mdi-content-save</v-icon>
            </v-btn>
          </div>
        </template>
        <span>{{ !isDirty ? $t('configurationForm.noChanges') : !isValid ? $t('configurationForm.fixValidation') : $t('configurationForm.save') }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn
              small
              depressed
              :disabled="isDefaultValue"
              min-width="0"
              class="px-1"
              @click="showResetConfirm = true"
            >
              <v-icon small>mdi-restore</v-icon>
            </v-btn>
          </div>
        </template>
        <span>{{ isDefaultValue ? $t('configurationForm.alreadyDefault') : $t('configurationForm.resetToDefault') }}</span>
      </v-tooltip>
    </div>

    <!-- Reset confirmation dialog -->
    <v-dialog v-model="showResetConfirm" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">{{ $t('configurationForm.resetConfirmTitle') }}</v-card-title>
        <v-card-text>
          {{ $t('configurationForm.resetConfirmText') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showResetConfirm = false">{{ $t('configurationForm.cancel') }}</v-btn>
          <v-btn color="warning" text @click="onResetConfirmed">{{ $t('configurationForm.reset') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, reactive, ref, watch } from "vue";
import STDatePickerV2 from "../STDatePickerV2.vue";
import STTimePicker from "../STTimePicker.vue";
import STItemLookup from "../STItemLookup.vue";

interface ConfigHeader {
  id: number;
  code: string;
  name: string;
  description: string;
  input_type: string;
  required: boolean;
  multiple_value: boolean;
  default_value: string;
  options: string[] | string | null;
  input_options: Record<string, any> | string | null;
  reference_table: string;
}

interface EffectiveValue {
  code: string;
  effective_value: string;
  source_level: string;
  source_scope_id: number;
  own_value: string | null;
  editable: boolean;
}

interface Props {
  header: ConfigHeader;
  value: string;
  valueId: number;
  hotelId: number | string;
  configurationId: number;
  scopeType?: string;
  scopeId?: number;
  effective?: EffectiveValue;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  scopeType: "hotel",
  scopeId: 0,
});

const emit = defineEmits<{
  (e: "save", payload: { id: number; hotel_id: number | string; configuration_id: number; scope_type: string; scope_id: number; value: string }): void;
  (e: "clear-override", payload: { configuration_id: number; scope_type: string; scope_id: number }): void;
}>();

const ownValue = computed(() => props.effective?.own_value ?? null);

const isReadonly = computed(() => props.readonly || (!!props.effective && !props.effective.editable));

const inheritedLevel = computed(() => {
  if (!props.effective || ownValue.value !== null) return "";
  return props.effective.source_level || "";
});

const canClearOverride = computed(() =>
  !!props.effective &&
  props.effective.editable &&
  ownValue.value !== null &&
  props.effective.source_level !== "system"
);

const onClearOverride = () => {
  emit("clear-override", {
    configuration_id: props.configurationId,
    scope_type: props.scopeType,
    scope_id: props.scopeId,
  });
};

const instance = getCurrentInstance();
const http = instance?.proxy?.$http;
const t = (key: string, fallback?: string) => String(instance?.proxy?.$t?.(key) || fallback || key);

const localValue = ref(props.value || "");
const boolValue = ref(props.value === "true");
const checkboxValues = ref<string[]>(props.value ? props.value.split(",").filter(Boolean) : []);
const isDirty = ref(false);
const showResetConfirm = ref(false);
const selectedFile = ref<File | null>(null);
const uploading = ref(false);

const parseDateTimeParts = (val: string) => {
  if (!val) return { date: "", time: "" };
  // Supports "YYYY-MM-DD HH:mm" or "YYYY-MM-DDTHH:mm"
  const parts = val.split(/[T ]/);
  return { date: parts[0] || "", time: parts[1] || "" };
};
const dateTimeParts = reactive(parseDateTimeParts(props.value || ""));

const extractDateOnly = (val: any): string => {
  if (!val) return "";
  return String(val).split(/[T ]/)[0];
};

// savedValue stores the last persisted value for dirty comparison.
// For date/datetime types, normalize so picker emissions match.
const normalizeSavedValue = (val: string, type: string): string => {
  if (!val) return "";
  if (type === "date") return extractDateOnly(val);
  if (type === "datetime") {
    const parts = parseDateTimeParts(val);
    return parts.date && parts.time ? `${parts.date} ${parts.time}` : "";
  }
  return val;
};
const savedValue = ref(normalizeSavedValue(props.value || "", props.header.input_type));

const onDateInput = (val: any) => {
  const newDate = extractDateOnly(val);
  // Ignore emissions that don't actually change the date
  if (newDate === extractDateOnly(savedValue.value)) {
    localValue.value = newDate;
    return;
  }
  localValue.value = newDate;
  markDirty();
};

const onTimeInput = (val: any) => {
  const newTime = val || "";
  if (newTime === savedValue.value) {
    localValue.value = newTime;
    return;
  }
  localValue.value = newTime;
  markDirty();
};

const onDateTimeInput = () => {
  const datePart = extractDateOnly(dateTimeParts.date);
  dateTimeParts.date = datePart;
  let newValue = "";
  if (datePart && dateTimeParts.time) {
    newValue = `${datePart} ${dateTimeParts.time}`;
  }
  // Ignore emissions that don't actually change the combined value
  if (newValue === savedValue.value) {
    localValue.value = newValue;
    return;
  }
  localValue.value = newValue;
  markDirty();
};

watch(() => props.value, (v) => {
  localValue.value = v || "";
  savedValue.value = normalizeSavedValue(v || "", props.header.input_type);
  boolValue.value = v === "true";
  checkboxValues.value = v ? v.split(",").filter(Boolean) : [];
  const dt = parseDateTimeParts(v || "");
  dateTimeParts.date = dt.date;
  dateTimeParts.time = dt.time;
  isDirty.value = false;
  if (props.header.input_type === "table") {
    resolveTableDisplayText();
  }
});

const markDirty = () => {
  isDirty.value = true;
};

const isDefaultValue = computed(() => {
  const defaultVal = props.header.default_value ?? "";
  if (props.header.input_type === "bool") {
    return String(boolValue.value) === (defaultVal || "false");
  }
  if (props.header.input_type === "checkbox") {
    return checkboxValues.value.join(",") === defaultVal;
  }
  return localValue.value === defaultVal;
});

const labelWithRequired = computed(() => {
  return props.header.required ? `${props.header.name} *` : props.header.name;
});

const parsedOptions = computed((): string[] => {
  if (!props.header.options) return [];
  if (Array.isArray(props.header.options)) return props.header.options;
  try {
    return JSON.parse(props.header.options as string);
  } catch {
    return [];
  }
});

const inputOptions = computed((): Record<string, any> => {
  if (!props.header.input_options) return {};
  if (typeof props.header.input_options === "object") return props.header.input_options;
  try {
    return JSON.parse(props.header.input_options as string);
  } catch {
    return {};
  }
});

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldRules = computed(() => {
  const rules: ((v: any) => boolean | string)[] = [];
  const type = props.header.input_type;
  const opts = inputOptions.value;

  // Required
  if (props.header.required) {
    rules.push((v: any) => (v !== null && v !== undefined && v !== "") || t("configurationForm.required"));
  }

  // Email validation
  if (type === "email") {
    rules.push((v: string) => {
      if (!v) return true;
      const emails = v.split(",").map((e: string) => e.trim()).filter(Boolean);
      if (!props.header.multiple_value && emails.length > 1) return t("configurationForm.singleEmailOnly");
      for (const email of emails) {
        if (!emailRegex.test(email)) return `${t("configurationForm.invalidEmail")}: ${email}`;
      }
      return true;
    });
    if (opts.max_count && props.header.multiple_value) {
      rules.push((v: string) => !v || v.split(",").filter(Boolean).length <= opts.max_count || t("configurationForm.maxEmails").replace("{count}", opts.max_count));
    }
  }

  // Integer validation
  if (type === "int") {
    rules.push((v: string) => !v || !isNaN(Number(v)) || t("configurationForm.invalidNumber"));
    if (opts.min != null) {
      rules.push((v: string) => !v || Number(v) >= opts.min || t("configurationForm.minValue").replace("{min}", opts.min));
    }
    if (opts.max != null) {
      rules.push((v: string) => !v || Number(v) <= opts.max || t("configurationForm.maxValue").replace("{max}", opts.max));
    }
  }

  // String / textarea max length
  if ((type === "string" || type === "textarea") && opts.max_length) {
    rules.push((v: string) => !v || v.length <= opts.max_length || t("configurationForm.maxLength").replace("{max}", opts.max_length));
  }

  // Datetime — require both date and time
  if (type === "datetime") {
    rules.push(() => {
      if (!dateTimeParts.date && !dateTimeParts.time) return true;
      if (!dateTimeParts.date) return t("configurationForm.dateRequired");
      if (!dateTimeParts.time) return t("configurationForm.timeRequired");
      return true;
    });
  }

  // Select / radio — must be a valid option
  if ((type === "select" || type === "radio") && parsedOptions.value.length > 0) {
    rules.push((v: string) => !v || parsedOptions.value.includes(v) || t("configurationForm.invalidSelection"));
  }

  return rules;
});

const emitSave = (val: string) => {
  emit("save", {
    id: props.valueId,
    hotel_id: props.hotelId,
    configuration_id: props.configurationId,
    scope_type: props.scopeType,
    scope_id: props.scopeId,
    value: val,
  });
};

const isValid = computed(() => {
  const val = props.header.input_type === "bool" ? String(boolValue.value) : localValue.value;
  for (const rule of fieldRules.value) {
    const result = rule(val);
    if (result !== true) return false;
  }
  return true;
});

const onSave = () => {
  if (!isDirty.value) return;
  if (!isValid.value) return;
  if (props.header.input_type === "bool") {
    emitSave(String(boolValue.value));
  } else {
    emitSave(localValue.value);
  }
  isDirty.value = false;
};

const onCheckboxChange = () => {
  if (!props.header.multiple_value && checkboxValues.value.length > 1) {
    // Single value only — keep only the last selected
    checkboxValues.value = [checkboxValues.value[checkboxValues.value.length - 1]];
  }
  localValue.value = checkboxValues.value.join(",");
  markDirty();
};

const onResetConfirmed = () => {
  showResetConfirm.value = false;
  const defaultVal = props.header.default_value ?? "";
  localValue.value = defaultVal;
  boolValue.value = defaultVal === "true";
  checkboxValues.value = defaultVal ? defaultVal.split(",").filter(Boolean) : [];
  const dt = parseDateTimeParts(defaultVal);
  dateTimeParts.date = dt.date;
  dateTimeParts.time = dt.time;
  isDirty.value = true;
};

// File upload
const fileName = computed(() => {
  if (!localValue.value) return "";
  return localValue.value.split("/").pop() || localValue.value;
});

const fileRules = computed(() => {
  const rules: ((v: any) => boolean | string)[] = [];
  const opts = inputOptions.value;
  if (opts.max_size_mb) {
    rules.push((v: File) => !v || v.size <= opts.max_size_mb * 1024 * 1024 || t("configurationForm.maxFileSize").replace("{max}", opts.max_size_mb));
  }
  return rules;
});

const onFileSelected = async (file: File | null) => {
  if (!file || !http) return;
  const opts = inputOptions.value;
  if (opts.max_size_mb && file.size > opts.max_size_mb * 1024 * 1024) return;

  uploading.value = true;
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", "configuration");
    formData.append("hotel_id", String(props.hotelId));

    const res = await http.post("/file/v1", formData, { contentType: "multipart/form-data" });
    const bucketKey = res?.data?.Data;
    if (bucketKey) {
      localValue.value = bucketKey;
      markDirty();
    }
  } catch {
    instance?.proxy?.$archNotify?.({ color: "error", message: t("configurationForm.uploadFailed") });
  } finally {
    uploading.value = false;
    selectedFile.value = null;
  }
};

const onClearFile = () => {
  localValue.value = "";
  selectedFile.value = null;
  markDirty();
};

const onDownloadFile = () => {
  if (!localValue.value) return;
  const base = (process.env.VUE_APP_STORAGE_STATIC_URL || "").replace(/\/+$/, "");
  const key = localValue.value.replace(/^\/+/, "");
  window.open(`${base}/${key}`, "_blank");
};

// Table (item lookup)
const tableDisplayText = ref("");

const tableLookupHeaders = computed(() => {
  const opts = inputOptions.value;
  if (opts.columns && Array.isArray(opts.columns)) {
    return opts.columns;
  }
  return [
    { text: t("configurationForm.code"), value: "code", width: "150px" },
    { text: t("configurationForm.name"), value: "name" },
  ];
});

const tableLookupDisplayKey = computed(() => {
  const opts = inputOptions.value;
  return opts.display_key || "name";
});

const tableLookupStoreKey = computed(() => {
  const opts = inputOptions.value;
  return opts.store_key || "id";
});

const onTableItemSelected = (item: any) => {
  const storeKey = tableLookupStoreKey.value;
  const displayKey = tableLookupDisplayKey.value;
  const newValue = item?.[storeKey] != null ? String(item[storeKey]) : "";
  tableDisplayText.value = item?.[displayKey] != null ? String(item[displayKey]) : "";
  if (newValue !== localValue.value) {
    localValue.value = newValue;
    markDirty();
  }
};

// Resolve display text for table lookup on load
const resolveTableDisplayText = async () => {
  if (props.header.input_type !== "table" || !localValue.value || !http || !props.header.reference_table) return;
  const storeKey = tableLookupStoreKey.value;
  const displayKey = tableLookupDisplayKey.value;
  try {
    const url = `/hotel/${props.hotelId}/dto/${props.header.reference_table}/v1?filter_by[]=${storeKey}&filter_by_operator[]=1&filter_by_value[]=${localValue.value}&page_size=1`;
    const res = await http.get(url);
    const item = res?.data?.Data?.[0];
    if (item && item[displayKey]) {
      tableDisplayText.value = String(item[displayKey]);
    } else {
      tableDisplayText.value = localValue.value;
    }
  } catch {
    tableDisplayText.value = localValue.value;
  }
};

if (props.header.input_type === "table" && props.value) {
  resolveTableDisplayText();
}
</script>
